import {
  IonButton,
  IonContent,
  IonItem,
  IonLabel,
  IonList,
  IonPage,
  IonSegment,
  IonSegmentButton,
  IonTextarea,
  useIonToast,
} from "@ionic/react";
import { Checkbox, Chip, CircularProgress, FormControlLabel, TextField, Typography } from "@material-ui/core";
import MuiAutocomplete from "@material-ui/lab/Autocomplete";
import { DatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import MomentUtils from "@date-io/moment";
import moment, { Moment } from "moment";
import { useEffect, useState } from "react";
import Header from "src/components/Header";
import LoginAPI from "src/services/login";
import Operarios from "src/services/operarios";
import PuntoDeServicioAPI from "src/services/puntoDeServicio";
import RegistroCapacitacionAPI, {
  Tema,
  RegistroMultiplePayload,
  TipoCapacitacion,
  TIPOS_CAPACITACION,
  etiquetaTipo,
} from "src/services/registroCapacitacion";

interface OperarioItem {
  legajo: string;
  nombre: string;
  cargo: string;
}

interface PsItem {
  ps_codigo: string;
  ps_nombre: string;
}

const campo: React.CSSProperties = { marginBottom: 16, width: "100%" };

const RegistroCapacitacion: React.FC = () => {
  const [present] = useIonToast();

  // Tipos que el usuario puede registrar (capacitador técnico -> TECNICA,
  // admins de Seguridad -> SEGURIDAD). Con uno solo no se muestra el selector.
  const [tipos, setTipos] = useState<TipoCapacitacion[]>([]);
  const [tipo, setTipo] = useState<TipoCapacitacion | null>(null);
  const esSeguridad = tipo === "SEGURIDAD";
  // Técnica con casos puntuales de colaboradores administrativos: busca en todo
  // el padrón de funcionarios activos, como Seguridad.
  const [incluirAdministrativos, setIncluirAdministrativos] = useState(false);
  const buscaFuncionarios = esSeguridad || incluirAdministrativos;

  const [fechaCapa, setFechaCapa] = useState<Moment | null>(moment());
  const [operarioSeleccionado, setOperarioSeleccionado] = useState<OperarioItem | null>(null);
  const [psSeleccionado, setPsSeleccionado] = useState<PsItem | null>(null);
  const [temasSeleccionados, setTemasSeleccionados] = useState<Tema[]>([]);
  const [comentario, setComentario] = useState("");

  const [temas, setTemas] = useState<Tema[]>([]);

  const [operarioInput, setOperarioInput] = useState("");
  const [operarioOpciones, setOperarioOpciones] = useState<OperarioItem[]>([]);
  const [operarioCargando, setOperarioCargando] = useState(false);

  const [psInput, setPsInput] = useState("");
  const [psOpciones, setPsOpciones] = useState<PsItem[]>([]);
  const [psCargando, setPsCargando] = useState(false);

  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [nombreUsuario, setNombreUsuario] = useState("");

  useEffect(() => {
    const init = async () => {
      try {
        const [rol, meRes] = await Promise.all([
          RegistroCapacitacionAPI.miRol(),
          LoginAPI.me(),
        ]);
        setTipos(rol.tipos_registro);
        setTipo(rol.tipos_registro[0] || null);
        if (rol.tipos_registro.length === 0) setCargando(false);
        setNombreUsuario(meRes?.data?.datos?.emp_nombre || "");
      } catch (err: any) {
        present({
          message: err?.response?.data?.error || "Error al cargar los datos iniciales",
          duration: 4000,
          color: "danger",
        });
        setCargando(false);
      }
    };
    init();
  }, [present]);

  // Temas del tipo elegido; al cambiar de tipo se limpia lo que depende de él.
  useEffect(() => {
    if (!tipo) return;
    let activo = true;
    setCargando(true);
    setTemasSeleccionados([]);
    setOperarioSeleccionado(null);
    setOperarioInput("");
    setOperarioOpciones([]);
    setIncluirAdministrativos(false);
    setPsSeleccionado(null);
    setPsInput("");
    RegistroCapacitacionAPI.listarTemas(tipo)
      .then((res) => { if (activo) setTemas(res.data?.datos || []); })
      .catch((err: any) => {
        if (!activo) return;
        setTemas([]);
        present({
          message: err?.response?.data?.error || "Error al cargar los temas",
          duration: 4000,
          color: "danger",
        });
      })
      .finally(() => { if (activo) setCargando(false); });
    return () => { activo = false; };
  }, [tipo, present]);

  // Técnica: operarios y supervisores (o cualquier funcionario si se incluyen
  // administrativos). Seguridad: cualquier funcionario activo (mínimo 3 letras,
  // o números de cédula / legajo).
  useEffect(() => {
    const texto = operarioInput.trim();
    const minimo = buscaFuncionarios && !/^\d+$/.test(texto) ? 3 : 2;
    if (texto.length < minimo) {
      setOperarioOpciones([]);
      return;
    }
    let activo = true;
    setOperarioCargando(true);
    const timer = setTimeout(async () => {
      try {
        const arr: any[] = buscaFuncionarios
          ? (await RegistroCapacitacionAPI.buscarFuncionarios(texto)).filter((f) => f.legajo)
          : await Operarios.listarOperariosSupervisores(texto, true);
        if (!activo) return;
        setOperarioOpciones(
          arr.map((o: any) => ({
            legajo: String(o.legajo ?? ""),
            nombre: String(o.nombre ?? ""),
            cargo: String(o.cargo ?? ""),
          }))
        );
      } catch {
        if (!activo) return;
        setOperarioOpciones([]);
      } finally {
        if (!activo) return;
        setOperarioCargando(false);
      }
    }, 350);
    return () => { activo = false; clearTimeout(timer); };
  }, [operarioInput, buscaFuncionarios]);

  useEffect(() => {
    let activo = true;
    setPsCargando(true);
    const timer = setTimeout(async () => {
      try {
        const res = await PuntoDeServicioAPI.listar(psInput.trim() || undefined);
        if (!activo) return;
        const lista: PsItem[] = (res.data?.datos || []).map((p: any) => ({
          ps_codigo: String(p.ps_codigo ?? p.codigo ?? ""),
          ps_nombre: String(p.ps_nombre ?? p.nombre ?? ""),
        }));
        setPsOpciones(lista.slice(0, 80));
      } catch {
        if (!activo) return;
        setPsOpciones([]);
      } finally {
        if (!activo) return;
        setPsCargando(false);
      }
    }, 350);
    return () => { activo = false; clearTimeout(timer); };
  }, [psInput]);

  // En Seguridad el lugar puede ser un PS o un texto libre (ej. oficina central).
  const ubicacion = psSeleccionado?.ps_nombre || (esSeguridad ? psInput.trim() : "");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tipo) return;
    if (!fechaCapa) {
      present({ message: "Seleccione la fecha de capacitación", duration: 3000, color: "warning" });
      return;
    }
    if (!operarioSeleccionado) {
      present({
        message: buscaFuncionarios ? "Seleccione un funcionario" : "Seleccione un operario o supervisor",
        duration: 3000,
        color: "warning",
      });
      return;
    }
    if (!ubicacion) {
      present({
        message: esSeguridad ? "Indique el lugar de la capacitación" : "Seleccione el punto de servicio",
        duration: 3000,
        color: "warning",
      });
      return;
    }
    if (temasSeleccionados.length === 0) {
      present({ message: "Seleccione al menos un tema de capacitación", duration: 3000, color: "warning" });
      return;
    }
    if (!comentario.trim()) {
      present({ message: "El comentario es obligatorio", duration: 3000, color: "warning" });
      return;
    }

    setGuardando(true);
    try {
      const payload: RegistroMultiplePayload = {
        Tipo: tipo,
        FechaCapa: fechaCapa.toISOString(),
        Legajo: operarioSeleccionado.legajo,
        TemaIds: temasSeleccionados.map((t) => t.Id),
        NombreUbicacion: ubicacion,
        PsCodigo: psSeleccionado?.ps_codigo,
        Comentario: comentario.trim(),
      };
      const res = await RegistroCapacitacionAPI.registrarMultiple(payload);
      present({
        message: res.data?.info || "Capacitaciones registradas correctamente",
        duration: 4000,
        color: "success",
      });
      setOperarioSeleccionado(null);
      setOperarioInput("");
      setPsSeleccionado(null);
      setPsInput("");
      setTemasSeleccionados([]);
      setComentario("");
      setFechaCapa(moment());
    } catch (err: any) {
      present({
        message: err?.response?.data?.error || "No se pudo registrar la capacitación",
        duration: 4000,
        color: "danger",
      });
    } finally {
      setGuardando(false);
    }
  };

  return (
    <IonPage>
      <Header titulo={tipos.length === 1 ? `Registro de Capacitación ${etiquetaTipo(tipo || "")}` : "Registro de Capacitación"} />
      <IonContent id="registro-capacitacion">
        <form
          onSubmit={handleSubmit}
          style={{
            padding: "16px",
            width: "100%",
            maxWidth: 600,
            margin: "0 auto",
            boxSizing: "border-box",
          }}
        >

          {tipos.length > 1 && (
            <IonSegment
              value={tipo || undefined}
              onIonChange={(e) => setTipo(e.detail.value as TipoCapacitacion)}
              disabled={guardando}
              style={{ marginBottom: 16 }}
            >
              {TIPOS_CAPACITACION.filter((t) => tipos.includes(t.value)).map((t) => (
                <IonSegmentButton key={t.value} value={t.value}>
                  <IonLabel>{t.label}</IonLabel>
                </IonSegmentButton>
              ))}
            </IonSegment>
          )}

          {!cargando && tipos.length === 0 && (
            <Typography color="textSecondary" style={{ marginBottom: 16 }}>
              No tenés permiso para registrar capacitaciones.
            </Typography>
          )}

          <div style={campo}>
            <MuiPickersUtilsProvider utils={MomentUtils}>
              <DatePicker
                label="Fecha de capacitación"
                value={fechaCapa}
                onChange={(date) => setFechaCapa(date)}
                format="DD/MM/YYYY"
                disabled={guardando}
                fullWidth
                inputVariant="outlined"
                cancelLabel="Cancelar"
                okLabel="Aceptar"
              />
            </MuiPickersUtilsProvider>
          </div>

          <div style={campo}>
            {tipo === "TECNICA" && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={incluirAdministrativos}
                    onChange={(e) => {
                      setIncluirAdministrativos(e.target.checked);
                      setOperarioSeleccionado(null);
                      setOperarioInput("");
                      setOperarioOpciones([]);
                    }}
                    color="primary"
                    disabled={guardando}
                  />
                }
                label="Incluir administrativos"
              />
            )}
            <MuiAutocomplete
              style={{ width: "100%" }}
              options={operarioOpciones}
              getOptionLabel={(o) => `${o.nombre} (${o.legajo}) - ${o.cargo}`}
              getOptionSelected={(a, b) => a.legajo === b.legajo}
              value={operarioSeleccionado}
              onChange={(_, val) => setOperarioSeleccionado(val)}
              inputValue={operarioInput}
              onInputChange={(_, val, reason) => {
                if (reason !== "reset" || val !== "") setOperarioInput(val);
              }}
              loading={operarioCargando}
              loadingText="Buscando..."
              noOptionsText={
                operarioInput.trim().length < (buscaFuncionarios ? 3 : 2)
                  ? `Escriba al menos ${buscaFuncionarios ? 3 : 2} caracteres`
                  : "Sin coincidencias"
              }
              filterOptions={(opts) => opts}
              disabled={guardando || !tipo}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={buscaFuncionarios ? "Funcionario (nombre, cédula o legajo)" : "Operario / Supervisor"}
                  variant="outlined"
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {operarioCargando ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </div>

          <div style={campo}>
            <MuiAutocomplete<PsItem, false, false, boolean>
              style={{ width: "100%" }}
              freeSolo={esSeguridad}
              options={psOpciones}
              getOptionLabel={(o) => (typeof o === "string" ? o : o.ps_nombre)}
              getOptionSelected={(a, b) => a.ps_codigo === b.ps_codigo}
              value={psSeleccionado}
              onChange={(_, val) => setPsSeleccionado(val && typeof val !== "string" ? val : null)}
              inputValue={psInput}
              onInputChange={(_, val, reason) => {
                if (reason !== "reset" || val !== "") setPsInput(val);
                // Texto libre (Seguridad): si se edita, deja de ser el PS elegido.
                if (reason === "input" && psSeleccionado && val !== psSeleccionado.ps_nombre) {
                  setPsSeleccionado(null);
                }
              }}
              loading={psCargando}
              loadingText="Buscando..."
              noOptionsText="Sin coincidencias"
              filterOptions={(opts) => opts}
              disabled={guardando || !tipo}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={esSeguridad ? "Lugar (punto de servicio u otro)" : "Punto de servicio"}
                  variant="outlined"
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {psCargando ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </div>

          <div style={campo}>
            <MuiAutocomplete
              multiple
              style={{ width: "100%" }}
              options={temas}
              getOptionLabel={(t) => t.Tema}
              getOptionSelected={(a, b) => a.Id === b.Id}
              groupBy={(t) => t.Categoria}
              value={temasSeleccionados}
              onChange={(_, val) => setTemasSeleccionados(val)}
              loading={cargando}
              loadingText="Cargando temas..."
              noOptionsText="Sin coincidencias"
              disabled={cargando || guardando || !tipo}
              disableCloseOnSelect
              renderTags={(value) =>
                value.length === 0 ? null : (
                  <Chip
                    size="small"
                    label={
                      value.length === 1
                        ? value[0].Tema.length > 20
                          ? `${value[0].Tema.slice(0, 20)}…`
                          : value[0].Tema
                        : `${value.length} temas seleccionados`
                    }
                    style={{ maxWidth: "80%", margin: 2 }}
                  />
                )
              }
              renderOption={(t, { selected }) => (
                <span style={{ whiteSpace: "normal", lineHeight: 1.4 }}>
                  {selected ? "✓ " : ""}{t.Tema}
                </span>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Temas de capacitación"
                  variant="outlined"
                  fullWidth
                  placeholder={temasSeleccionados.length === 0 ? "Buscar y seleccionar..." : ""}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {cargando ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            {/* Lista de temas seleccionados debajo del campo */}
            {temasSeleccionados.length > 0 && (
              <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 4 }}>
                {temasSeleccionados.map((t) => (
                  <div
                    key={t.Id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      background: "#f5f5f5",
                      borderRadius: 4,
                      padding: "4px 8px",
                      fontSize: 13,
                    }}
                  >
                    <span style={{ flex: 1 }}>{t.Tema}</span>
                    <Chip
                      size="small"
                      label="✕"
                      disabled={guardando}
                      onClick={() =>
                        setTemasSeleccionados((prev) => prev.filter((x) => x.Id !== t.Id))
                      }
                      style={{ marginLeft: 8, minWidth: 28, cursor: "pointer" }}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {nombreUsuario ? (
            <Typography
              variant="caption"
              color="textSecondary"
              style={{ display: "block", marginBottom: 8 }}
            >
              Capacitador: {nombreUsuario}
            </Typography>
          ) : null}

          <IonList style={{ margin: "0 -16px" }}>
            <IonItem>
              <IonLabel position="stacked">Comentario *</IonLabel>
              <IonTextarea
                value={comentario}
                disabled={guardando}
                autoGrow
                rows={3}
                placeholder="Observaciones sobre la capacitación"
                onIonChange={(e) => setComentario(e.detail.value || "")}
              />
            </IonItem>
          </IonList>

          <IonButton
            expand="block"
            type="submit"
            style={{ marginTop: 16 }}
            disabled={guardando || cargando || !tipo}
          >
            {guardando ? "Guardando..." : "Registrar capacitación"}
          </IonButton>

        </form>
      </IonContent>
    </IonPage>
  );
};

export default RegistroCapacitacion;
